import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useDownloadCart } from '../services/downloadCart';
import { useAgentAlias } from '../services/agentAliasContext';
import RecordingDetail from '../components/RecordingDetail';

const SYSTEM_LABELS: Record<string, string> = {
  pulsedial: 'PulseDial',
  vicidial: 'Vicidial (Outbound)',
  dialedin: 'DialedIn (Medicare Inbound)',
  leadmomentum: 'LeadMomentum (GHL)',
  '3cx-legacy': '3CX (Decommissioned)',
};

type DatePreset = 'all' | 'today' | 'yesterday' | '7days' | '30days' | 'custom';

function SystemView({ systemIdProp, showSystemFilter }: { systemIdProp?: string; showSystemFilter?: boolean } = {}) {
  const params = useParams<{ systemId: string }>();
  const [systemFilterValue, setSystemFilterValue] = useState('');
  const systemId = systemIdProp || params.systemId || systemFilterValue || undefined;
  const { addItem, addItems, removeItem, isInCart, isFull } = useDownloadCart();
  const { resolveAgent } = useAgentAlias();

  // --- Server-side pagination state ---
  const [pageData, setPageData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  // True when the last full fetch errored — render "—" instead of a stale
  // count or a misleadingly-healthy "0 recording(s)".
  const [countFailed, setCountFailed] = useState(false);
  const [nextKey, setNextKey] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);

  // --- Filter state ---
  const [phoneSearch, setPhoneSearch] = useState('');
  const [appliedPhone, setAppliedPhone] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [hideShortCalls, setHideShortCalls] = useState(true);
  const [datePreset, setDatePreset] = useState<DatePreset>('30days');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [showFilters, setShowFilters] = useState(false);

  // --- Filter options from server ---
  const [filterOptions, setFilterOptions] = useState<{ campaigns: string[]; agents: string[] }>({ campaigns: [], agents: [] });

  // --- UI state ---
  const [setup, setSetup] = useState<any>(null);
  const [selectedRecording, setSelectedRecording] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkTranscribing, setBulkTranscribing] = useState(false);
  const [transcribingKeys, setTranscribingKeys] = useState<Set<string>>(new Set());
  const [completedKeys, setCompletedKeys] = useState<Set<string>>(new Set());

  // Track whether this is a "next page" request (skip count)
  const skipCountRef = useRef(false);
  // Monotonic request sequence — responses from superseded requests are
  // ignored so an out-of-order (stale) response can't clobber newer state.
  const requestSeqRef = useRef(0);

  // Date range from preset
  const getDateRange = useCallback(() => {
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    const week = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
    const month = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];

    switch (datePreset) {
      case 'today': return { from: today, to: today };
      case 'yesterday': return { from: yesterday, to: yesterday };
      case '7days': return { from: week, to: today };
      case '30days': return { from: month, to: today };
      case 'custom': return { from: customFrom, to: customTo };
      default: return { from: '2020-01-01', to: '2030-12-31' };
    }
  }, [datePreset, customFrom, customTo]);

  // Fetch SFTP setup
  useEffect(() => {
    if (!systemId || showSystemFilter) { setSetup(null); return; }
    api.getSystemSetup(systemId).then(setSetup).catch(() => null);
  }, [systemId, showSystemFilter]);

  // Reset filters and fetch new filter options when system changes
  useEffect(() => {
    setAgentFilter('');
    setCampaignFilter('');
    setDirectionFilter('');
    setAppliedPhone('');
    setPhoneSearch('');
    setDatePreset('30days');
    setHideShortCalls(true);
    api.getSearchFilters({ systemId }).then(data => {
      setFilterOptions({
        campaigns: data.campaigns || [],
        agents: data.agents || [],
      });
    }).catch(() => {});
  }, [systemId]);

  // --- Main data fetch ---
  const fetchPage = useCallback(async (cursor: string | null, skipCount: boolean) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    if (!skipCount) setCountLoading(true);

    try {
      const { from, to } = getDateRange();
      if (!from || !to) { setLoading(false); setCountLoading(false); return; }

      const searchParams: any = {
        dateFrom: from,
        dateTo: to,
        pageSize,
      };
      if (systemId) searchParams.systemId = systemId;
      if (appliedPhone) searchParams.phone = appliedPhone;
      if (agentFilter) searchParams.agent = agentFilter;
      if (campaignFilter) searchParams.campaign = campaignFilter;
      if (directionFilter) searchParams.direction = directionFilter;
      if (hideShortCalls) searchParams.hideShortCalls = 'true';
      if (cursor) searchParams.nextKey = cursor;
      if (skipCount) searchParams.skipCount = 'true';

      const data = await api.searchPaginated(searchParams);
      if (seq !== requestSeqRef.current) return; // stale response — a newer request superseded this one
      setPageData(data.recordings || []);
      setNextKey(data.nextKey || null);
      if (data.totalCount !== null && data.totalCount !== undefined) {
        setTotalCount(data.totalCount);
        setCountFailed(false);
        setCountLoading(false);
      } else if (!skipCount) {
        // Backend returned no count (null when a pre-aggregated count is
        // missing) — clear the spinner so "Counting..." can't stick.
        setTotalCount(null);
        setCountFailed(false);
        setCountLoading(false);
      }
    } catch {
      if (seq !== requestSeqRef.current) return;
      setPageData([]);
      setNextKey(null);
      // Don't retain the previous filter's count on error — show "—".
      setTotalCount(null);
      setCountFailed(true);
      setCountLoading(false);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [getDateRange, systemId, appliedPhone, agentFilter, campaignFilter, directionFilter, hideShortCalls, pageSize]);

  // Fetch first page when filters change
  useEffect(() => {
    setCursorStack([null]);
    setCurrentPage(0);
    setSelectedIds(new Set());
    skipCountRef.current = false;
    fetchPage(null, false);
  }, [fetchPage]);

  // --- Phone search ---
  const handlePhoneSearch = () => {
    const cleaned = phoneSearch.replace(/\D/g, '');
    if (cleaned.length >= 7) {
      setAppliedPhone(cleaned);
    }
  };
  const clearPhoneSearch = () => {
    setPhoneSearch('');
    setAppliedPhone('');
  };

  // --- Pagination ---
  const handleNextPage = () => {
    if (!nextKey) return;
    setCursorStack(prev => [...prev, nextKey]);
    setCurrentPage(prev => prev + 1);
    setSelectedIds(new Set());
    fetchPage(nextKey, true);
  };

  const handlePrevPage = () => {
    if (currentPage <= 0) return;
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1] || null;
    setCursorStack(newStack);
    setCurrentPage(prev => prev - 1);
    setSelectedIds(new Set());
    fetchPage(prevCursor, true);
  };

  // --- Bulk select ---
  const toggleSelect = (i: number) => {
    const next = new Set(selectedIds);
    next.has(i) ? next.delete(i) : next.add(i);
    setSelectedIds(next);
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === pageData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(pageData.map((_, i) => i)));
  };

  const handleBulkTranscribe = async () => {
    setBulkTranscribing(true);
    const toTranscribe = Array.from(selectedIds).map(i => pageData[i]).filter(r => !r.hasTranscript && !r.transcript);
    for (const r of toTranscribe) {
      try {
        const key = getRecordingKey(r);
        await api.startTranscription(key);
      } catch { /* continue */ }
    }
    setBulkTranscribing(false);
    setSelectedIds(new Set());
    alert(`${toTranscribe.length} transcription(s) queued. They'll appear shortly.`);
  };

  // Active filter chips
  const activeFilters: { label: string; onRemove: () => void }[] = [];
  if (appliedPhone) activeFilters.push({ label: `Phone: ${appliedPhone}`, onRemove: clearPhoneSearch });
  if (agentFilter) activeFilters.push({ label: `Agent: ${resolveAgent(agentFilter)}`, onRemove: () => setAgentFilter('') });
  if (datePreset !== 'all') {
    const labels: Record<string, string> = { today: 'Today', yesterday: 'Yesterday', '7days': 'Last 7 Days', '30days': 'Last 30 Days', custom: `${customFrom} to ${customTo}` };
    activeFilters.push({ label: labels[datePreset] || datePreset, onRemove: () => setDatePreset('all') });
  }
  if (directionFilter) activeFilters.push({ label: `Direction: ${directionFilter}`, onRemove: () => setDirectionFilter('') });
  if (campaignFilter) activeFilters.push({ label: `Campaign: ${formatCampaign(campaignFilter)}`, onRemove: () => setCampaignFilter('') });
  if (hideShortCalls) activeFilters.push({ label: 'Hide short calls (<10s)', onRemove: () => setHideShortCalls(false) });

  // Poll for transcription completion
  useEffect(() => {
    if (transcribingKeys.size === 0) return;
    const interval = setInterval(async () => {
      const newCompleted = new Set(completedKeys);
      const stillProcessing = new Set<string>();
      for (const key of Array.from(transcribingKeys)) {
        try {
          const result = await api.getTranscriptResult(key);
          if (result.status === 'COMPLETED' || result.status === 'FAILED') {
            newCompleted.add(key);
          } else {
            stillProcessing.add(key);
          }
        } catch {
          stillProcessing.add(key);
        }
      }
      setCompletedKeys(newCompleted);
      setTranscribingKeys(stillProcessing);
    }, 10000);
    return () => clearInterval(interval);
  }, [transcribingKeys, completedKeys]);

  return (
    <div>
      <div className="page-header">
        <h1>{showSystemFilter ? 'All Recordings' : (SYSTEM_LABELS[systemId || ''] || systemId || 'Recordings')}</h1>
        {showSystemFilter && <p>Search and browse recordings across all phone systems</p>}
      </div>

      {setup && setup.connection && (
        <div className="setup-card" style={{ marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: 15 }}>
            SFTP Connection
            <span className={`status badge ${setup.active ? 'badge-success' : 'badge-warning'}`} style={{ marginLeft: 8 }}>
              {setup.active ? 'Active' : 'Inactive'}
            </span>
          </h3>
          <div style={{ display: 'flex', gap: 24, fontSize: 13, color: '#555' }}>
            <span><strong>Host:</strong> <code>{setup.connection.host}</code></span>
            <span><strong>Port:</strong> <code>{setup.connection.port}</code></span>
            <span><strong>User:</strong> <code>{setup.connection.username}</code></span>
            <span><strong>Upload:</strong> <code>{setup.connection.uploadFolder}</code></span>
          </div>
        </div>
      )}

      {/* ===== FILTER BAR ===== */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Phone Search */}
          <div style={{ display: 'flex', flex: 1, minWidth: 240 }}>
            <input
              type="text"
              placeholder="Search by phone number..."
              value={phoneSearch}
              onChange={e => setPhoneSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handlePhoneSearch(); }}
              style={{ flex: 1, minWidth: 0, padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: '8px 0 0 8px', fontSize: 14, outline: 'none' }}
            />
            <button onClick={handlePhoneSearch}
              style={{ flexShrink: 0, whiteSpace: 'nowrap', padding: '10px 16px', border: '1px solid #e0e0e0', borderLeft: 'none', borderRadius: '0 8px 8px 0', fontSize: 14, background: '#00bcd4', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
              Search
            </button>
          </div>
          {/* System (only on All Recordings) */}
          {showSystemFilter && (
            <select value={systemFilterValue} onChange={e => setSystemFilterValue(e.target.value)}
              style={{ padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, background: '#fff', minWidth: 140 }}>
              <option value="">All Systems</option>
              <option value="dialedin">DialedIn (Medicare)</option>
              <option value="pulsedial">PulseDial</option>
              <option value="vicidial">Vicidial</option>
              <option value="leadmomentum">LeadMomentum</option>
              <option value="3cx-legacy">3CX Legacy</option>
            </select>
          )}
          {/* Agent */}
          <select value={agentFilter} onChange={e => setAgentFilter(e.target.value)}
            style={{ padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, background: '#fff', minWidth: 140 }}>
            <option value="">All Agents</option>
            {filterOptions.agents.map(a => <option key={a} value={a}>{resolveAgent(a)}</option>)}
          </select>
          {/* Campaign */}
          {filterOptions.campaigns.length > 0 && (
            <select value={campaignFilter} onChange={e => setCampaignFilter(e.target.value)}
              style={{ padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, background: '#fff', minWidth: 140 }}>
              <option value="">All Campaigns</option>
              {filterOptions.campaigns.map(c => <option key={c} value={c}>{formatCampaign(c)}</option>)}
            </select>
          )}
          {/* Date Range */}
          <select value={datePreset} onChange={e => setDatePreset(e.target.value as DatePreset)}
            style={{ padding: '10px 14px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, background: '#fff', minWidth: 140 }}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="custom">Custom Range</option>
          </select>
          {datePreset === 'custom' && (
            <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                style={{ padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14 }} />
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                style={{ padding: '10px 12px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14 }} />
            </>
          )}
          {/* Filters button */}
          <button onClick={() => setShowFilters(!showFilters)}
            style={{ padding: '10px 16px', border: '1px solid #e0e0e0', borderRadius: 8, fontSize: 14, background: showFilters ? '#f0f0f0' : '#fff', cursor: 'pointer' }}>
            Filters {activeFilters.length > 0 ? `(${activeFilters.length})` : ''}
          </button>
        </div>

        {/* Filters popover */}
        {showFilters && (
          <div style={{ marginTop: 12, padding: '12px 16px', background: '#fafafa', borderRadius: 8, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={hideShortCalls} onChange={e => setHideShortCalls(e.target.checked)} />
              Hide short calls (&lt;10s)
            </label>
            <select value={directionFilter} onChange={e => setDirectionFilter(e.target.value)}
              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}>
              <option value="">Any Direction</option>
              <option value="in">Inbound</option>
              <option value="out">Outbound</option>
              <option value="int">Internal</option>
            </select>
          </div>
        )}

        {/* Phone search banner */}
        {appliedPhone && (
          <div style={{ marginTop: 10, padding: '8px 14px', background: '#e0f7fa', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span>Showing results for <strong>{appliedPhone}</strong> across all time</span>
            <button onClick={clearPhoneSearch}
              style={{ background: 'none', border: 'none', color: '#00838f', cursor: 'pointer', fontWeight: 600 }}>Clear</button>
          </div>
        )}

        {/* Active filter chips */}
        {activeFilters.length > 0 && !appliedPhone && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {activeFilters.map((f, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: '#e8f0fe', color: '#1a56db', borderRadius: 16, fontSize: 12, fontWeight: 500 }}>
                {f.label}
                <button onClick={f.onRemove} style={{ background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>&times;</button>
              </span>
            ))}
            <button onClick={() => { setAgentFilter(''); setDatePreset('30days'); setDirectionFilter(''); setCampaignFilter(''); setHideShortCalls(false); clearPhoneSearch(); }}
              style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 12 }}>
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* ===== RESULTS BAR ===== */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 13, color: '#666' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>
            {countLoading ? (
              <span style={{ color: '#999' }}>Counting...</span>
            ) : totalCount !== null ? (
              <strong>{totalCount.toLocaleString()} recording(s)</strong>
            ) : countFailed ? (
              <span style={{ color: '#999' }} title="Count unavailable — the last fetch failed">&mdash;</span>
            ) : (
              <span>{pageData.length} recording(s)</span>
            )}
          </span>
          {pageData.length > 0 && (
            <button onClick={() => {
              const source = selectedIds.size > 0 ? Array.from(selectedIds).map(i => pageData[i]).filter(Boolean) : pageData;
              const headers = ['Call Date', 'Time', 'Agent', 'Phone', 'Direction', 'Duration', 'Size', 'Filename', 'Transcript', 'Type'];
              const rows = source.map(r => [
                (r.callDate || r.recordDate) || '', extractTime(r), r.agentId || '', extractPhone(r),
                r.direction || '', r.duration || '', r.fileSizeBytes || '',
                r.originalFilename || '', r.hasTranscript ? 'Yes' : 'No',
                r.callType === 'voicemail' ? 'Voicemail' : 'Call',
              ]);
              const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
              a.download = `recordings-${systemId || 'all'}-${new Date().toISOString().split('T')[0]}.csv`;
              a.click(); URL.revokeObjectURL(a.href);
            }}
              style={{ padding: '5px 12px', background: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
              Export CSV ({selectedIds.size > 0 ? `${selectedIds.size} selected` : `page of ${pageData.length}`})
            </button>
          )}
          {selectedIds.size > 0 && (
            <>
              <button onClick={handleBulkTranscribe} disabled={bulkTranscribing}
                style={{ padding: '5px 12px', background: '#00bcd4', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {bulkTranscribing ? 'Queuing...' : `Transcribe ${selectedIds.size} selected`}
              </button>
              <button onClick={() => {
                const items = Array.from(selectedIds).map(i => {
                  const r = pageData[i];
                  if (!r) return null;
                  return {
                    recordingId: `${r.PK}|${r.SK}`,
                    agentId: r.agentId || '',
                    callerId: r.callerId || '',
                    recordDate: (r.callDate || r.recordDate) || '',
                    systemId: r.systemId || systemId || '',
                    duration: r.duration ? formatDuration(r.duration) : estimateDuration(r.fileSizeBytes, r.systemId || systemId) || undefined,
                    hasTranscript: !!(r.hasTranscript || r.transcript),
                    originalFilename: r.originalFilename,
                  };
                }).filter(Boolean) as any[];
                const added = addItems(items);
                if (added > 0) alert(`${added} recording(s) added to download cart`);
                else alert('Selected recordings are already in cart or cart is full (20 max)');
              }}
                disabled={isFull}
                style={{ padding: '5px 12px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: isFull ? 'not-allowed' : 'pointer', opacity: isFull ? 0.5 : 1 }}>
                Add {selectedIds.size} to Cart
              </button>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Show:</span>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); }}
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* ===== TABLE ===== */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={selectedIds.size === pageData.length && pageData.length > 0} onChange={toggleSelectAll} />
                </th>
                <th>Call Date</th>
                <th>Time</th>
                {showSystemFilter && <th>System</th>}
                <th>Agent</th>
                <th>Phone Number</th>
                <th>Dir</th>
                <th>Duration</th>
                <th>Size</th>
                <th>Campaign</th>
                <th>Transcript</th>
                <th style={{ width: 36 }} title="Download Cart"></th>
              </tr>
            </thead>
            <tbody>
              {pageData.map((r, i) => (
                <tr key={i} onClick={() => setSelectedRecording(r)} style={{ cursor: 'pointer', background: selectedIds.has(i) ? '#f0f4ff' : undefined }}>
                  <td onClick={e => { e.stopPropagation(); toggleSelect(i); }}>
                    <input type="checkbox" checked={selectedIds.has(i)} readOnly />
                  </td>
                  <td>
                    {r.callDate || r.recordDate}
                    {(r.callType === 'voicemail' || (r.originalFilename || '').startsWith('vmail_')) && (
                      <span style={{ marginLeft: 6, padding: '2px 6px', background: '#f3e5f5', color: '#7b1fa2', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>VM</span>
                    )}
                  </td>
                  <td>{extractTime(r)}</td>
                  {showSystemFilter && <td><span className={`system-tag ${r.systemId === '3cx-legacy' ? 'legacy' : r.systemId}`}>{r.systemId}</span></td>}
                  <td>{resolveAgent(r.agentId, r)}</td>
                  <td>{extractPhone(r)}</td>
                  <td>{r.direction && r.direction !== 'unk' ? r.direction : '-'}</td>
                  <td>{r.duration ? formatDuration(r.duration) : estimateDuration(r.fileSizeBytes, r.systemId || systemId)}</td>
                  <td>{r.fileSizeBytes ? formatSize(r.fileSizeBytes) : '-'}</td>
                  <td>{formatCampaign(r.campaign) || '-'}</td>
                  <td onClick={e => e.stopPropagation()}>
                    {(() => {
                      const key = getRecordingKey(r);
                      const isComplete = r.hasTranscript || r.transcript || completedKeys.has(key);
                      const isProcessing = transcribingKeys.has(key);

                      if (isComplete) {
                        return (
                          <button onClick={() => setSelectedRecording(r)}
                            style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            View / Download
                          </button>
                        );
                      }
                      if (isProcessing) {
                        return (
                          <span style={{ color: '#f39c12', fontSize: 12, fontWeight: 600 }}>Processing...</span>
                        );
                      }
                      return (
                        <button onClick={async () => {
                          try {
                            await api.startTranscription(key);
                            setTranscribingKeys(prev => new Set(prev).add(key));
                          } catch { /* */ }
                        }}
                          style={{ background: '#00bcd4', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          Transcribe
                        </button>
                      );
                    })()}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    {(() => {
                      const rid = `${r.PK}|${r.SK}`;
                      const inCart = isInCart(rid);
                      return (
                        <button
                          onClick={() => {
                            if (inCart) {
                              removeItem(rid);
                            } else {
                              addItem({
                                recordingId: rid,
                                agentId: r.agentId || '',
                                callerId: r.callerId || '',
                                recordDate: (r.callDate || r.recordDate) || '',
                                systemId: r.systemId || systemId || '',
                                duration: r.duration ? formatDuration(r.duration) : estimateDuration(r.fileSizeBytes, r.systemId || systemId) || undefined,
                                hasTranscript: !!(r.hasTranscript || r.transcript),
                                originalFilename: r.originalFilename,
                              });
                            }
                          }}
                          disabled={!inCart && isFull}
                          title={inCart ? 'Remove from cart' : isFull ? 'Cart full (20 max)' : 'Add to download cart'}
                          style={{
                            background: 'none', border: 'none', cursor: (!inCart && isFull) ? 'not-allowed' : 'pointer',
                            fontSize: 16, padding: '2px 6px', opacity: (!inCart && isFull) ? 0.3 : 1,
                            color: inCart ? '#27ae60' : '#999',
                          }}
                        >
                          {inCart ? '\u2714' : '+'}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr><td colSpan={showSystemFilter ? 12 : 11} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No recordings found</td></tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 16, fontSize: 14 }}>
            <button onClick={handlePrevPage} disabled={currentPage === 0}
              style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 6, cursor: currentPage === 0 ? 'default' : 'pointer', background: currentPage === 0 ? '#f5f5f5' : '#fff' }}>
              Previous
            </button>
            <span style={{ color: '#666' }}>Page {currentPage + 1}</span>
            <button onClick={handleNextPage} disabled={!nextKey}
              style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 6, cursor: !nextKey ? 'default' : 'pointer', background: !nextKey ? '#f5f5f5' : '#fff' }}>
              Next
            </button>
          </div>
        </>
      )}

      {selectedRecording && (
        <RecordingDetail recording={selectedRecording} onClose={() => setSelectedRecording(null)} />
      )}
    </div>
  );
}

function getRecordingKey(r: any): string {
  if (r.archivePath) {
    const match = r.archivePath.match(/\/volume1\/archive\/([^/]+)\/archive\/(.+)/);
    if (match) return `${match[1]}/${match[2]}`;
  }
  return `${r.systemId}/${r.normalizedFilename || r.originalFilename}`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function extractPhone(r: any): string {
  if (r.callerPhone) {
    const d = r.callerPhone;
    if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    return d;
  }
  const fname = r.originalFilename || '';
  const match = fname.match(/\+?(\d{10,15})/);
  if (match) {
    const digits = match[1];
    if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
    if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    return digits;
  }
  return r.callerId && r.callerId !== 'unknown' && r.callerId !== '0000' ? r.callerId : '-';
}

function extractTime(r: any): string {
  const fname = r.originalFilename || '';
  // Prefer the call date-time. Vicidial writes it dash-separated as
  // YYYYMMDD-HHMMSS and prepends underscore-separated upload stamps — matching
  // the dash form first gets the CALL time, not the ingest/upload time (the
  // old single pattern needed 14 consecutive digits, missed the dash format,
  // and fell through to ingestTimestamp). The second pattern preserves the
  // original behavior for any system whose filename has no separator.
  const match = fname.match(/(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/)
    || fname.match(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/);
  if (match) {
    let h = parseInt(match[4]);
    const m = match[5];
    const ampm = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return `${h}:${m} ${ampm}`;
  }
  if (r.ingestTimestamp) {
    try { return new Date(r.ingestTimestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }); } catch {}
  }
  return '-';
}

function formatDuration(seconds: number | string): string {
  const s = typeof seconds === 'string' ? parseInt(seconds) : seconds;
  if (!s || isNaN(s)) return '-';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// Per-system filesize-to-seconds divisor for the estimate fallback. After
// 2026-05-19 every new DialedIn row gets real duration from the restored
// mediainfo probe in ingest-recordings.py and every Vicidial puller row
// gets it from MP3-header parsing, so this fallback effectively only fires
// for genuinely-broken rows. Values reflect actual stored bitrates verified
// via ffprobe:
//   Vicidial   ~16 kbps   ->  2000 B/s
//   DialedIn   ~64 kbps   ->  8000 B/s  (NOT 128 kbps -- vendor WAV is 128
//                                        kbps but our MP3 transcode is 64)
//   Default    ~128 kbps  -> 16000 B/s  (legacy assumption for everything else)
function bytesPerSecForSystem(systemId: string | undefined | null): number {
  if (systemId === 'vicidial') return 2000;
  if (systemId === 'dialedin') return 8000;
  return 16000;
}

function estimateDuration(bytes: number | null, systemId?: string | null): string {
  if (!bytes) return '-';
  const seconds = Math.round(bytes / bytesPerSecForSystem(systemId));
  if (seconds < 1) return '-';
  return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
}

function formatCampaign(campaign: string): string {
  if (!campaign) return '';
  return campaign.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default SystemView;
